import React, { useEffect, useRef, useState } from 'react'
import { Button, Checkbox, notification, Table, Typography } from 'antd'
import { ColumnProps } from 'antd/lib/table'
import { Formik } from 'formik'
import { useTranslation } from 'react-i18next'

import {
  Id,
  PolicyFlowConference,
  PolicyFlowFormat,
  PolicyFlowType
} from 'src/types'
import { Tooltip } from 'src/components/Ant'
import * as PoliciesService from 'src/services/api/policies-flow'

import * as CSS from './styles'
import * as Values from './initial-values'
import FlowConferencesModal from './flow-conference-modal'
import sanitize from './sanitize'
import validationSchema from './validation-schema'

type PolicyFlowConferenceList = PolicyFlowConference & { key: Id }

export default function ConferenceFlow() {
  const [dataSource, setDataSource] = useState<PolicyFlowConferenceList[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [modalPolicyFlowFormat, setModalPolicyFlowFormat] =
    useState<PolicyFlowFormat>(PolicyFlowFormat.STANDARD)
  const [modalPolicyFlowType, setModalPolicyFlowType] =
    useState<PolicyFlowType>(PolicyFlowType.HIERARCHY)

  const refFormik = useRef(null)
  const isSubscribed = useRef(true)
  const { t } = useTranslation()

  useEffect(
    () => () => {
      isSubscribed.current = false
    },
    []
  )

  useEffect(() => {
    setIsLoading(true)
    PoliciesService.getPolicyConferences()
      .then(response => {
        if (isSubscribed.current) {
          if (response.status === 200) {
            const conferences: any[] =
              response.data?.policy_conferences.map(data => ({
                ...data,
                //@ts-ignore
                key: data.policy_conference_id
              })) || []
            setDataSource(conferences)
          } else {
            notification.error({
              message: t('common.response.status-500'),
              placement: 'bottomRight'
            })
          }
        }
      })
      .catch(response => {
        notification.error({
          message: t('common.response.status-500'),
          placement: 'bottomRight'
        })
        //Send Bugsnag?
      })
      .finally(() => {
        if (isSubscribed.current) {
          setIsLoading(false)
        }
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const columns: ColumnProps<PolicyFlowConferenceList>[] = [
    {
      align: 'center',
      dataIndex: 'code',
      render: (code: string) => {
        if (code?.length > 50) {
          return <Tooltip title={code}>{`${code.substring(0, 47)}...`}</Tooltip>
        }
        return <Typography.Text>{code}</Typography.Text>
      },
      title: (
        <Typography.Text strong>
          {t('web.resources.common.code.title')}
        </Typography.Text>
      ),
      width: 120
    },
    {
      align: 'center',
      dataIndex: 'name',
      render: (name: string) => {
        if (name?.length > 50) {
          return <Tooltip title={name}>{`${name.substring(0, 47)}...`}</Tooltip>
        }
        return <Typography.Text>{name}</Typography.Text>
      },
      title: (
        <Typography.Text strong>
          {t('web.resources.common.name.title')}
        </Typography.Text>
      )
    },
    {
      align: 'center',
      dataIndex: '',
      render: (record: PolicyFlowConference) => {
        const { cost_center_conference, reason_conference } = record

        if (cost_center_conference) {
          return (
            <Typography.Text>
              {t('web.policies-flow.type.cost-center')}
            </Typography.Text>
          )
        }

        if (reason_conference) {
          return (
            <Typography.Text>
              {t('web.policies-flow.type.reason')}
            </Typography.Text>
          )
        }

        if (!cost_center_conference && !reason_conference) {
          return (
            <Typography.Text>
              {t('web.policies-flow.type.hierarchy')}
            </Typography.Text>
          )
        }

        return ''
      },
      title: (
        <Typography.Text strong>
          {t('web.policies-flow.conference.type.title')}
        </Typography.Text>
      )
    },
    {
      align: 'center',
      dataIndex: '',
      render: (record: PolicyFlowConference) => {
        const { automatic_conference, consensual_conference } = record

        if (automatic_conference) {
          return (
            <Typography.Text>
              {t('web.policies-flow.format.automatic')}
            </Typography.Text>
          )
        }

        if (consensual_conference) {
          return (
            <Typography.Text>
              {t('web.policies-flow.format.consensus')}
            </Typography.Text>
          )
        }

        if (!automatic_conference && !consensual_conference) {
          return (
            <Typography.Text>
              {t('web.policies-flow.format.standard')}
            </Typography.Text>
          )
        }

        return ''
      },
      title: (
        <Typography.Text strong>
          {t('web.policies-flow.conference.format.title')}
        </Typography.Text>
      )
    },
    {
      align: 'center',
      dataIndex: 'exception_checker',
      render: (value: boolean) => <Checkbox checked={value} disabled />,
      title: (
        <Typography.Text strong>
          {t('web.policies-flow.exception-modal.title')}
        </Typography.Text>
      ),
      width: 120
    },
    {
      align: 'center',
      dataIndex: '',
      render: record => (
        <CSS.Switch
          style={{ width: '100%' }}
          checked={record.active}
          onClick={() => {
            setIsLoading(true)
            PoliciesService.SavePolicyConferences({
              ...record,
              active: !record.active
            })
              .then(response => {
                if (isSubscribed.current) {
                  if (response.status === 200) {
                    const record = response.data.policy_conferences[0]
                    setDataSource(data =>
                      data.map(d =>
                        d.policy_conference_id === record.policy_conference_id
                          ? { ...record, key: record.policy_conference_id }
                          : d
                      )
                    )
                  } else {
                    notification.error({
                      message: t('common.response.status-500'),
                      placement: 'bottomRight'
                    })
                  }
                }
              })
              .catch(response => {
                notification.error({
                  message: t('common.response.status-500'),
                  placement: 'bottomRight'
                })
              })
              .finally(() => {
                if (isSubscribed.current) {
                  setIsLoading(false)
                }
              })
          }}
        />
      ),
      title: <Typography.Text strong>{t('common.active')}</Typography.Text>,
      width: 100
    },
    {
      align: 'center',
      dataIndex: '',
      render: (record: PolicyFlowConference) => (
        <Button
          icon="edit"
          onClick={() => {
            refFormik.current?.resetForm(record)

            let type = PolicyFlowType.HIERARCHY
            let format = PolicyFlowFormat.STANDARD

            if (record.automatic_conference) {
              format = PolicyFlowFormat.AUTOMATIC
            }
            if (record.consensual_conference) {
              format = PolicyFlowFormat.CONSENSUS
            }

            if (record.cost_center_conference) {
              type = PolicyFlowType.COST_CENTER
            }
            if (record.reason_conference) {
              type = PolicyFlowType.REASON
            }

            setModalPolicyFlowFormat(format)
            setModalPolicyFlowType(type)
          }}
          type="ghost"
        />
      ),
      title: (
        <Typography.Text strong>{t('common.actions.edit')}</Typography.Text>
      ),
      width: 120
    }
  ]

  function handleSubmit(values: PolicyFlowConference) {
    setIsLoading(true)
    const sanitized = sanitize(values)
    PoliciesService.SavePolicyConferences(sanitized)
      .then(response => {
        if (isSubscribed.current) {
          if (response.status === 200) {
            const record = response.data.policy_conferences[0]
            if (typeof values.policy_conference_id === 'string') {
              //is new
              setDataSource(data => [
                ...data,
                { ...record, key: record.policy_conference_id }
              ])
            } else {
              //is an update
              setDataSource(data =>
                data.map(d =>
                  d.policy_conference_id === record.policy_conference_id
                    ? { ...record, key: record.policy_conference_id }
                    : d
                )
              )
            }
            refFormik.current.resetForm(Values.initialValues)
          } else {
            notification.error({
              message: t('common.response.status-500'),
              placement: 'bottomRight'
            })
          }
        }
      })
      .catch(response => {
        notification.error({
          message: t('common.response.status-500'),
          placement: 'bottomRight'
        })
      })
      .finally(() => {
        if (isSubscribed.current) {
          setIsLoading(false)
        }
      })
  }

  function ButtonAdd() {
    return (
      <Button
        icon="plus"
        type="primary"
        onClick={() => {
          if (refFormik.current) {
            refFormik.current.resetForm(Values.defaultValue)
            setModalPolicyFlowFormat(PolicyFlowFormat.STANDARD)
            setModalPolicyFlowType(PolicyFlowType.HIERARCHY)
          }
        }}
      >
        {t('common.actions.add')}
      </Button>
    )
  }
  return (
    <>
      <CSS.ListTitle>
        <span>{t('web.policies-flow.list-title')}</span>
        <ButtonAdd />
      </CSS.ListTitle>
      <Table
        className="table-striped"
        columns={columns}
        dataSource={dataSource}
        loading={isLoading}
        locale={{
          emptyText: t('common.no-data-found')
        }}
        pagination={false}
      />
      <br />
      <CSS.ButtonContainer>
        <ButtonAdd />
      </CSS.ButtonContainer>
      <Formik
        initialValues={Values.initialValues}
        onSubmit={handleSubmit}
        ref={refFormik}
        render={formikProps => {
          if (!formikProps.values.policy_conference_id) {
            return <span />
          }

          const isVisible = Boolean(formikProps?.values?.policy_conference_id)

          return (
            <FlowConferencesModal
              formikProps={formikProps}
              isLoading={isLoading}
              isVisible={isVisible}
              modalPolicyFlowFormat={modalPolicyFlowFormat}
              modalPolicyFlowType={modalPolicyFlowType}
              refFormik={refFormik}
              setModalPolicyFlowFormat={setModalPolicyFlowFormat}
              setModalPolicyFlowType={setModalPolicyFlowType}
            />
          )
        }}
        validationSchema={validationSchema}
      />
    </>
  )
}
